import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {camelCase} from 'lodash';
import {Observable} from 'rxjs';
import {tap} from 'rxjs/operators';

import {environment} from '../../../environments/environment';
import {SfAnalyticsInterface} from '../models/sf-analytics.interface';
import {SfObjectDocumentationInterface} from '../models/sf-object-documentation.interface';
import {SfAnalyticsStore} from './sf-analytics-store';

@Injectable({
  providedIn: 'root'
})
export class SfAnalyticsService {
  queryParamsForCharts: any;

  constructor(
    private http: HttpClient,
    private sfAnalyticsStore: SfAnalyticsStore
  ) {
    this.queryParamsForCharts = {};
  }

  fetchSfAnalytics(refModelId): any {
    const url = `${environment.API_ENDPOINT}/refmodel/${refModelId}/sfAnalytics`;
    return this.http.get(url).pipe(
      tap((analytics: SfAnalyticsInterface) => {
        this.sfAnalyticsStore.set('sfAnalytics', analytics);
      })
    );
  }
  // deprecated
  fetchDocumentationByObjectsStat(refModelId) {
    const url = `${environment.API_ENDPOINT}/refmodel/${refModelId}/sf-analytics/objects-documentation`;
    return this.http.get(url).pipe(
      tap((analytics: SfObjectDocumentationInterface) => {
        this.sfAnalyticsStore.set('sfObjectDocumentation', analytics);
      })
    );
  }

  getSfAnalyticsForPeriod(refModelId, duration, type) {
    this.sfAnalyticsStore.set('bigChartIsLoading', true);
    const url = `${environment.API_ENDPOINT}/refmodel/${refModelId}/sf-analytics/stat`;
    return this.http.get(url, {params: {duration, type}}).pipe(
      tap((data: any) => {
        this.sfAnalyticsStore.set('bigChartIsLoading', false);
      })
    );
  }

  getGdprStatistic(refModelId, queryParams?) {
    /** If duration is not provided it returns current statuses
     *  If type is not provided it returns stat by gdpr statuses */
    this.sfAnalyticsStore.set('bigChartIsLoading', true);
    const url = `${environment.API_ENDPOINT}/refmodel/${refModelId}/gdpr/stat`;
    return this.http.get(url, {params: queryParams}).pipe(
      tap((data: any) => {
        this.sfAnalyticsStore.set('bigChartIsLoading', false);
      })
    );
  }

  getPercentageFilledByObjectsStat(refModelId: string): Observable<any> {
    this.sfAnalyticsStore.set('bigChartIsLoading', true);
    const url = `${environment.API_ENDPOINT}/refmodel/${refModelId}/sf-analytics/percentage-filled`;
    return this.http.get(url).pipe(
      tap((data: any) => {
        this.sfAnalyticsStore.set('bigChartIsLoading', false);
      })
    );
  }

  exportToPdf(
    refModelId: string,
    orgDetails: any,
    refModelName: string,
    activeChart: string
  ): Observable<any> {
    const bodyObject = {
      orgDetails,
      queryParams: this.queryParamsForCharts,
      refModelName,
      activeChart
    };
    const url = `${environment.API_ENDPOINT}/refmodel/${refModelId}/sf-analytics/exportPdf`;
    return this.http.post(url, bodyObject);
  }

  updateQueryParamsForCharts(
    chart: string,
    filter: any,
    fromRoute?: boolean,
    queryParams?: boolean
  ) {
    if (fromRoute) {
      this.queryParamsForCharts = queryParams;
    } else {
      this.queryParamsForCharts[camelCase(chart)] = filter;
    }
  }

  getQueryParamsForChart(chart): any {
    return this.queryParamsForCharts[camelCase(chart)];
  }

  getDataForChart(refModelId: string, type: string): Observable<any> {
    this.sfAnalyticsStore.set('bigChartIsLoading', true);
    const url = `${environment.API_ENDPOINT}/refmodel/${refModelId}/sf-analytics/${type}`;
    return this.http.get(url).pipe(
      tap((data: any) => {
        this.sfAnalyticsStore.cachedData[type] = data;
        this.sfAnalyticsStore.set('bigChartIsLoading', false);
      })
    );
  }

  getDocumentationCharts(refModelId: string, type: string): Observable<any> {
    this.sfAnalyticsStore.set('bigChartIsLoading', true);
    const url = `${environment.API_ENDPOINT}/refmodel/${refModelId}/sf-analytics/object-documentation/${type}`;
    return this.http.get(url).pipe(
      tap((data: any) => {
        this.sfAnalyticsStore.cachedData.sfObjectDocumentation[type] = data;
        this.sfAnalyticsStore.set('bigChartIsLoading', false);
      })
    );
  }

  captureLastSeenChart(refModelId: string, chartType: string): Observable<any> {
    const url = `${environment.API_ENDPOINT}/refmodel/${refModelId}/charts`;

    return this.http.post(url, {chartType});
  }
}
